import { ExternalLink, FileCode, Printer } from "lucide-react";
import { ArtifactCardShell } from "@/components/chat/ArtifactCardShell";
import { openHtmlPreview } from "@/components/chat/HtmlPreviewPanel";
import { PublishAppButton } from "@/components/chat/PublishAppButton";
import { getApiBaseUrl } from "@/config/runtime";
import type { StreamingArtifact } from "@/hooks/useChatStreaming";
import { getHeaders } from "@/lib/api";
import { microAppService } from "@/lib/services/micro-apps";

interface HtmlArtifactCardProps {
  artifact: StreamingArtifact;
}

/**
 * Inline card rendered in the chat message list when an HTML document
 * artifact is generated. Clicking opens the HtmlPreviewPanel side panel.
 *
 * If the artifact was loaded from persisted message attachments (no inline
 * html_content), it fetches the HTML from the attachment endpoint on click.
 */
export function HtmlArtifactCard({ artifact }: Readonly<HtmlArtifactCardProps>) {
  const title = artifact.title || artifact.filename.replace(/\.html$/, "");
  const versionLabel = artifact.version && artifact.version > 1 ? `v${artifact.version}` : null;

  async function handleClick() {
    let htmlContent = artifact.html_content || "";

    // If no inline content, fetch from the attachment endpoint
    if (!htmlContent && artifact.attachment_uuid) {
      try {
        const baseUrl = getApiBaseUrl();
        const headers = await getHeaders();
        headers.delete("Content-Type");
        const response = await fetch(`${baseUrl}/chat/attachments/${artifact.attachment_uuid}/file`, { headers });
        if (response.ok) {
          htmlContent = await response.text();
        } else {
          console.error("Failed to fetch HTML content: HTTP", response.status);
          // Show error to user
          htmlContent = `<!DOCTYPE html><html><body style="font-family: system-ui; padding: 2rem; color: #666;"><h2>Failed to load HTML content</h2><p>HTTP ${response.status}: ${response.statusText}</p></body></html>`;
        }
      } catch (err) {
        console.error("Failed to fetch HTML content:", err);
        // Show error to user
        htmlContent = `<!DOCTYPE html><html><body style="font-family: system-ui; padding: 2rem; color: #666;"><h2>Failed to load HTML content</h2><p>${err instanceof Error ? err.message : "Network error"}</p></body></html>`;
      }
    }

    // Resolve the published app this attachment belongs to (if any) so the preview
    // panel can offer an "Open micro app" jump — persists across sessions, unlike the
    // in-session publish mapping.
    let appUuid: string | undefined;
    if (artifact.attachment_uuid) {
      try {
        const res = await microAppService.getAppForAttachment(artifact.attachment_uuid);
        appUuid = res.data.app_uuid ?? undefined;
      } catch {
        // Not published or not viewable — the panel just won't show the link.
      }
    }

    openHtmlPreview({
      title,
      htmlContent,
      attachmentUuid: artifact.attachment_uuid,
      url: artifact.url,
      version: artifact.version,
      lineageUuid: artifact.lineage_uuid,
      appUuid,
    });
  }

  function handlePrint(e: React.MouseEvent) {
    e.stopPropagation();
    if (artifact.html_content) {
      const blob = new Blob([artifact.html_content], { type: "text/html" });
      const url = URL.createObjectURL(blob);
      const win = window.open(url, "_blank");
      if (win) {
        win.addEventListener("afterprint", () => URL.revokeObjectURL(url));
        win.addEventListener("load", () => win.print());
      } else {
        URL.revokeObjectURL(url);
      }
    }
  }

  return (
    <ArtifactCardShell
      icon={FileCode}
      title={title}
      subtitle="HTML Document — Click to preview"
      versionLabel={versionLabel}
      onClick={handleClick}
      actions={
        <>
          <PublishAppButton attachmentUuid={artifact.attachment_uuid} title={title} />
          <button
            onClick={handlePrint}
            className="p-1.5 rounded hover:bg-interactive-hover text-content-tertiary hover:text-content-primary transition"
            title="Print / PDF"
          >
            <Printer className="w-3.5 h-3.5" />
          </button>
          <ExternalLink className="w-4 h-4 text-content-quaternary" />
        </>
      }
    />
  );
}
